// Invitation mail: default templates, variables, and preview rendering.
//
// The defaults live here (not in the DB): a study with no saved SFQ_EmailTemplate row
// falls back to these, so changing the wording for new studies is a frontend-only change.
//
// {{password}} is special. The access password is minted server-side inside the send
// transaction and only its hash is stored, so the browser can never know it — the body
// travels to the API with {{password}} still unresolved, and the preview shows a mask.

export const PASSWORD_MASK = "••••••••";

export const TEMPLATE_VARS = [
  { key: "piName",    label: "PI 이름" },
  { key: "siteName",  label: "Site 명" },
  { key: "publicUrl", label: "설문 링크", required: true },
  { key: "password",  label: "접속 비밀번호" },
  { key: "expiry",    label: "만료일" },
  { key: "fromName",  label: "보내는 사람" },
];

// The body is useless to the recipient without the questionnaire link — the composer
// blocks Send when it is missing, and the API rejects it too.
export const REQUIRED_VAR = "publicUrl";
export const hasRequiredVars = (bodyHtml) => String(bodyHtml || "").includes(`{{${REQUIRED_VAR}}}`);

// Matches SFQ_EmailLog.subject / SFQ_EmailTemplate.subject (NVARCHAR(300)). The API
// rejects longer subjects before sending, so cap it here rather than fail after the fact.
export const SUBJECT_MAX = 300;

const PASSWORD_BLOCK_STYLE =
  "font-size:18px;font-weight:700;letter-spacing:2px;background:#f1f3f5;" +
  "padding:10px 16px;display:inline-block;border-radius:4px;";

export const DEFAULT_TEMPLATES = {
  ko: {
    subject: "[CRO] 임상시험 실시기관 사전 타당성 조사 (SFQ) - {{siteName}}",
    bodyHtml: `<p>{{piName}} 선생님께,</p>
<p><b>{{siteName}}</b>의 임상시험 실시기관 사전 타당성 조사(SFQ)에 참여를 요청드립니다.</p>
<p>아래 링크를 통해 설문에 접속하실 수 있습니다. 링크는 <b>{{expiry}}</b>까지 유효합니다.</p>
<p><a href="{{publicUrl}}">{{publicUrl}}</a></p>
<p>링크 접속 후 아래 비밀번호를 입력하시면 설문을 확인하고 작성하실 수 있습니다.</p>
<p style="${PASSWORD_BLOCK_STYLE}">{{password}}</p>
<p>문의 사항이 있으시면 담당 CRA에게 연락 주시기 바랍니다.</p>
<br><p>감사합니다.<br>{{fromName}} 드림</p>
<hr><p style="font-size:11px;color:#888;">본 메일은 기밀 정보를 포함하고 있습니다. 잘못 수신하신 경우 삭제해 주시기 바랍니다.</p>`,
  },
  en: {
    subject: "[CRO] Site Feasibility Questionnaire - {{siteName}}",
    bodyHtml: `<p>Dear {{piName}},</p>
<p>You have been invited to complete a Site Feasibility Questionnaire (SFQ) for <b>{{siteName}}</b>.</p>
<p>Please access the questionnaire via the link below. The link will expire on <b>{{expiry}}</b>.</p>
<p><a href="{{publicUrl}}">{{publicUrl}}</a></p>
<p>When you open the link, enter the access password below to view and complete the questionnaire:</p>
<p style="${PASSWORD_BLOCK_STYLE}">{{password}}</p>
<p>If you have any questions, please contact your assigned CRA.</p>
<br><p>Best regards,<br>{{fromName}}</p>
<hr><p style="font-size:11px;color:#888;">This message is confidential. If received in error, please delete it.</p>`,
  },
};

// ── Embedded images ───────────────────────────────────────────────────────────
// A body's <img> point at this API (.../api/sfq/:sfqId/email-asset/:id) and are stored
// that way. The host in a stored URL is whichever API the template was authored against,
// so a template written in dev and read in prod would render broken images here — the
// path is the durable part, and this puts the current host back in front of it.
//
// The API matches by path too, so a stale host never breaks the actual send.
const API_ORIGIN = String(process.env.REACT_APP_BECCO_API_URL || "").replace(/\/+$/, "");
const ASSET_URL_RE = /(?:https?:\/\/[^/?#"'\s]+)?(\/api\/sfq\/\d+\/email-asset\/\d+)/g;

export const normalizeAssetUrls = (html) =>
  String(html ?? "").replace(ASSET_URL_RE, (_, assetPath) => `${API_ORIGIN}${assetPath}`);

const HTML_ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
const escapeHtml = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);

// Mirrors renderHtml/renderText in the API's services/Feasibility/email.js: {{key}} is
// substituted, unknown keys are left alone so a typo shows up here instead of vanishing.
// Keep the two in step — this is what makes the preview trustworthy.
const render = (tpl, vars, transform) =>
  String(tpl ?? "").replace(/\{\{(\w+)\}\}/g, (whole, key) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? transform(vars[key]) : whole
  );

export const renderPreviewHtml = (tpl, vars) => render(tpl, vars, escapeHtml);
export const renderPreviewText = (tpl, vars) => render(tpl, vars, (v) => String(v ?? ""));

// The expiry the mail will actually carry. A link whose date has already passed is
// renewed by the send itself (markInvitationSent), so the preview has to show the renewed
// date rather than the dead one — the same one-month-from-today, 23:59:59.999 KST window
// the API picks. Keep the two rules in step if either moves.
export function effectiveExpiry(inv) {
  const current = inv?.siteTokenExpiry ? new Date(inv.siteTokenExpiry) : null;
  if (current && !isNaN(current.getTime()) && current > new Date()) return current;

  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  const kstDate = new Date(d.getTime() + 9 * 3600 * 1000).toISOString().slice(0, 10);
  return new Date(`${kstDate}T23:59:59.999+09:00`);
}

// Whether this send will move the expiry — the composer says so before the CRA sends.
export const expiryWillRenew = (inv) =>
  !inv?.siteTokenExpiry || new Date(inv.siteTokenExpiry) <= new Date();

// Values for the preview. Everything but the password is known client-side.
export function buildPreviewVars({ inv, publicUrl, fromName, lang }) {
  const expiry = effectiveExpiry(inv)
    .toLocaleDateString(lang === "ko" ? "ko-KR" : "en-US",
                        { year: "numeric", month: "long", day: "numeric" });
  return {
    piName:   inv?.piName   || (lang === "ko" ? "PI 담당자" : "PI"),
    siteName: inv?.siteName || inv?.siteCode || (lang === "ko" ? "귀 기관" : "your site"),
    publicUrl: publicUrl || "",
    expiry,
    fromName: fromName || "CRO Clinical Team",
    password: PASSWORD_MASK,
  };
}

// Sample values for the template editor, which has no invitation to draw from.
export const SAMPLE_VARS = {
  ko: {
    piName: "홍길동", siteName: "서울대학교병원",
    publicUrl: "https://example.com/sfq-site/xxxx", expiry: "2026년 8월 13일",
    fromName: "김담당", password: PASSWORD_MASK,
  },
  en: {
    piName: "Dr. Hong", siteName: "Seoul National University Hospital",
    publicUrl: "https://example.com/sfq-site/xxxx", expiry: "August 13, 2026",
    fromName: "Kim CRA", password: PASSWORD_MASK,
  },
};
